/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import apiService from "@/services/apiService";
import useErrorStore from "@/store/confirmationStore";

const policeStationsCourtData = [
  {
    name: "मुगलसराय",
    courts: ["मुगलसराय"]
  },
  {
    name: "अलीनगर",
    courts: ["मुगलसराय", "सकलडीहा"]
  },
  {
    name: "बबुरी",
    courts: ["सदर", "मुगलसराय", "चकिया"]
  },
  {
    name: "एन्ट ह्यूमन ट्रैफिकिंग यूनिट",
    courts: []
  },
  {
    name: "महिला थाना",
    courts: []
  },
  {
    name: "चन्दौली",
    courts: ["सदर", "सकलडीहा", "मुगलसराय"]
  },
  {
    name: "सैयदराजा",
    courts: ["सदर", "सकलडीहा"]
  },
  {
    name: "कन्दवा",
    courts: ["सदर", "सकलडीहा"]
  },
  {
    name: "सकलड़ीहा",
    courts: ["सकलडीहा", "सदर"]
  },
  {
    name: "बलुआ",
    courts: ["सकलडीहा"]
  },
  {
    name: "धीना",
    courts: ["सदर", "सकलडीहा"]
  },
  {
    name: "धानापुर",
    courts: ["सकलडीहा"]
  },
  {
    name: "चकिया",
    courts: ["चकिया"]
  },
  {
    name: "शहाबगंज",
    courts: ["सदर", "चकिया"]
  },
  {
    name: "इलिया",
    courts: ["चकिया"]
  },
  {
    name: "महिला पुलिस परामर्श केन्द्र चकिया",
    courts: ["चकिया"]
  },
  {
    name: "नौगढ़",
    courts: ["नौगढ़"]
  },
  {
    name: "चकरघट्टा",
    courts: ["नौगढ़"]
  }
];

interface BondOrderPopupProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documentId: string;
  policeStation?: { name: string } | null;
  onSuccess?: () => void;
}

export function BondOrderPopup({ open, onOpenChange, documentId, policeStation, onSuccess }: BondOrderPopupProps) {
  const [formData, setFormData] = useState({
    dateOfOrder: "",
    effectiveFrom: "",
    effectiveTo: "",
    bondAmount: "",
    courtName: "",
    caseNumber: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const showError = useErrorStore((s) => s.showError);
  const [policeStationName, setPoliceStationName] = useState("");
  const [availableCourts, setAvailableCourts] = useState<string[]>([]);

  // Fetch document details to get police station
  useEffect(() => {
    const fetchDocumentDetails = async () => {
      if (documentId && open) {
        try {
          const policeStationName = policeStation ? policeStation.name : "";
          setPoliceStationName(policeStationName);

          // Find matching police station in the data
          const matchingStation = policeStationsCourtData.find(
            station => station.name === policeStationName
          );

          if (matchingStation) {
            setAvailableCourts(matchingStation.courts);
          } else {
            setAvailableCourts([]);
          }
        } catch (error) {
          console.error("Error fetching document details:", error);
        }
      }
    };

    fetchDocumentDetails();
  }, [documentId, open, policeStation]);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Auto-update effectiveTo when effectiveFrom changes
    if (field === "effectiveFrom" && value) {
      const fromDate = new Date(value);
      const toDate = new Date(fromDate);
      toDate.setFullYear(toDate.getFullYear() + 1);

      // Format date as YYYY-MM-DD for input[type="date"]
      const formattedToDate = toDate.toISOString().split('T')[0];
      setFormData((prev) => ({ ...prev, effectiveTo: formattedToDate }));
    }
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (!formData.dateOfOrder || !formData.effectiveFrom || !formData.effectiveTo ||
        !formData.bondAmount || !formData.courtName || !formData.caseNumber) {
      showError("All fields are required");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await apiService.post("crpc129-notice", JSON.stringify({
        documentId,
        dateOfOrder: new Date(formData.dateOfOrder).toISOString(),
        effectiveFrom: new Date(formData.effectiveFrom).toISOString(),
        effectiveTo: new Date(formData.effectiveTo).toISOString(),
        bondAmount: parseFloat(formData.bondAmount),
        courtName: formData.courtName,
        caseNumber: formData.caseNumber,
      }));

      if (response) {
        toast.success("Bond order created successfully!");
        onOpenChange(false);
        if (onSuccess) onSuccess();
        // Reset form
        setFormData({
          dateOfOrder: "",
          effectiveFrom: "",
          effectiveTo: "",
          bondAmount: "",
          courtName: "",
          caseNumber: "",
        });
      }
    } catch (error: any) {
      console.error("Error creating bond order:", error);
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Create Bond Order (CrPC 129 Notice)</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="courtName">Court Name *</Label>
              {availableCourts.length > 0 ? (
                <Select
                  value={formData.courtName}
                  onValueChange={(value) => handleInputChange("courtName", value)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select court name" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableCourts.map((court) => (
                      <SelectItem key={court} value={court}>
                        {court}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id="courtName"
                  value={formData.courtName}
                  onChange={(e) => handleInputChange("courtName", e.target.value)}
                  placeholder={policeStationName ? `No courts available for ${policeStationName}` : "Enter court name"}
                />
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="caseNumber">Case Number *</Label>
              <Input
                id="caseNumber"
                value={formData.caseNumber}
                onChange={(e) => handleInputChange("caseNumber", e.target.value)}
                placeholder="Enter case number"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="dateOfOrder">Date of Order *</Label>
              <Input
                id="dateOfOrder"
                type="date"
                value={formData.dateOfOrder}
                onChange={(e) => handleInputChange("dateOfOrder", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="bondAmount">Bond Amount *</Label>
              <Input
                id="bondAmount"
                type="number"
                step="0.01"
                value={formData.bondAmount}
                onChange={(e) => handleInputChange("bondAmount", e.target.value)}
                placeholder="Enter bond amount"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="effectiveFrom">Effective From *</Label>
              <Input
                id="effectiveFrom"
                type="date"
                value={formData.effectiveFrom}
                onChange={(e) => handleInputChange("effectiveFrom", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="effectiveTo">Effective To *</Label>
              <Input
                id="effectiveTo"
                type="date"
                value={formData.effectiveTo}
                onChange={(e) => handleInputChange("effectiveTo", e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Creating..." : "Create Bond Order"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
